import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Zap, Mail, Lock, User, ArrowRight, Loader2, Check, Crosshair, Skull, Eye, Shield, Target, Gift } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, loginWithGoogle, user } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralInfo, setReferralInfo] = useState(null);
  const [agreedTerms, setAgreedTerms] = useState(false);

  useEffect(() => {
    if (user) navigate('/dashboard');
    // Check for referral code in URL
    const ref = searchParams.get('ref') || searchParams.get('challenge');
    if (ref) {
      setReferralCode(ref.toUpperCase());
      validateReferral(ref.toUpperCase());
    }
  }, [user, navigate]);

  const validateReferral = async (code) => {
    try {
      const res = await axios.post(`${API}/referral/validate`, { code });
      if (res.data.valid) {
        setReferralInfo(res.data);
      }
    } catch (e) { /* ignore */ }
  };

  if (user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedTerms) {
      toast.error('You must agree to the Terms & Conditions');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      toast.error('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[0-9]/.test(password)) {
      toast.error('Password must contain at least one number');
      return;
    }
    setLoading(true);
    try {
      const demographics = {};
      if (age) demographics.age = parseInt(age);
      if (gender) demographics.gender = gender;
      const result = await register(email, password, name, demographics, referralCode || null);
      if (result.referral_applied) {
        toast.success('Account created with referral bonus! You and your friend each get a free month.');
      } else {
        toast.success('Account created! Welcome to Sub-Zero.');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex">
      <div className="fixed inset-0 cyber-grid opacity-30 pointer-events-none" />

      {/* Left Side - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-12 relative border-r border-border">
        <div className="absolute inset-0 bg-card/50" />
        <div className="absolute inset-0 scanline-bg" />
        <div className="relative z-10 max-w-md space-y-8">
          <div className="w-20 h-20 bg-primary/10 border border-primary/30 rounded flex items-center justify-center glow-primary">
            <Target className="w-10 h-10 text-primary" />
          </div>
          <h2 className="font-display text-2xl font-bold text-foreground">
            Start saving in under <span className="text-primary">60 seconds</span>
          </h2>

          <div className="space-y-4">
            {[
              { icon: <Crosshair className="w-4 h-4 text-primary" />, text: "One-tap subscription cancellation" },
              { icon: <Skull className="w-4 h-4 text-destructive" />, text: "Zombie trial protection — auto-kill before they charge" },
              { icon: <Eye className="w-4 h-4 text-yellow-500" />, text: "Auto-detect trials from Gmail and bank" },
              { icon: <Shield className="w-4 h-4 text-primary" />, text: "Bank-level 256-bit encryption" },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary/10 border border-primary/20 rounded flex items-center justify-center flex-shrink-0">
                  {item.icon}
                </div>
                <span className="text-muted-foreground text-sm">{item.text}</span>
              </div>
            ))}
          </div>

          <div className="bg-card/80 border border-primary/20 rounded p-4 noise-texture">
            <p className="text-sm text-muted-foreground italic">
              "Sub-Zero found 3 subscriptions I forgot about. Saved me <span className="text-primary font-bold font-mono">$47/month</span> instantly."
            </p>
            <p className="text-xs text-muted-foreground mt-2 font-mono">-- Sarah K., iOS User</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { value: "$412", label: "avg saved/yr" },
              { value: "47K+", label: "zombies killed" },
              { value: "< 4s", label: "to cancel" },
            ].map((stat, idx) => (
              <div key={idx} className="bg-card border border-border rounded p-3 text-center">
                <p className="text-lg font-display font-bold text-primary">{stat.value}</p>
                <p className="text-[10px] font-mono text-muted-foreground uppercase">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-12 py-12 relative z-20">
        <div className="w-full max-w-md mx-auto">
          <Link to="/" className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-primary rounded flex items-center justify-center glow-primary">
              <Zap className="w-6 h-6 text-black" />
            </div>
            <div>
              <span className="font-display font-bold text-lg text-foreground">SUB-ZERO</span>
              <span className="block text-[10px] font-mono text-primary tracking-widest">GHOST AUDITOR v2.0</span>
            </div>
          </Link>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/30 rounded mb-6">
            <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>
            <span className="text-primary text-xs font-mono tracking-wider">DEPLOY NEW AGENT</span>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-2 leading-tight">
            Create Your <span className="text-primary">Account</span>
          </h1>
          <p className="text-muted-foreground mb-8">
            Free scan. No credit card. Takes 60 seconds.
          </p>

          {/* Referral Banner */}
          {referralInfo && referralInfo.valid && (
            <div className="bg-primary/10 border border-primary/30 rounded p-3 mb-6 flex items-center gap-3" data-testid="referral-banner">
              <Gift className="w-5 h-5 text-primary flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-primary">{referralInfo.message}</p>
                <p className="text-xs text-muted-foreground">Both of you get 1 free month on any paid plan</p>
              </div>
            </div>
          )}

          <Button
            type="button" variant="outline"
            className="w-full mb-6 border-border text-foreground hover:bg-white/5 hover:border-primary/50 rounded h-12 font-medium transition-all"
            onClick={loginWithGoogle} data-testid="google-register-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-3 text-muted-foreground font-mono">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-foreground text-sm font-mono uppercase tracking-wider">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="name" type="text" placeholder="Agent Name"
                  value={name} onChange={(e) => setName(e.target.value)}
                  className="pl-10 bg-card border-border text-foreground placeholder:text-muted-foreground/50 rounded h-12 focus:border-primary"
                  required data-testid="name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground text-sm font-mono uppercase tracking-wider">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email" type="email" placeholder="agent@domain.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-card border-border text-foreground placeholder:text-muted-foreground/50 rounded h-12 focus:border-primary"
                  required data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground text-sm font-mono uppercase tracking-wider">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password" type="password" placeholder="Min. 8 chars, 1 uppercase, 1 number"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-card border-border text-foreground placeholder:text-muted-foreground/50 rounded h-12 focus:border-primary"
                  required minLength={8} data-testid="password-input"
                />
              </div>
            </div>

            {/* Optional Demographics */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="age" className="text-foreground text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Age (optional)</Label>
                <Input
                  id="age" type="number" placeholder="Age" min="13" max="120"
                  value={age} onChange={(e) => setAge(e.target.value)}
                  className="bg-card border-border text-foreground placeholder:text-muted-foreground/50 rounded h-10 text-sm"
                  data-testid="age-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gender" className="text-foreground text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Gender (optional)</Label>
                <select
                  id="gender" value={gender} onChange={(e) => setGender(e.target.value)}
                  className="w-full bg-card border border-border text-foreground rounded h-10 px-3 text-sm"
                  data-testid="gender-input"
                >
                  <option value="">Prefer not to say</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="terms-checkbox"
                checked={agreedTerms}
                onChange={(e) => setAgreedTerms(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-border accent-primary cursor-pointer"
                data-testid="terms-checkbox"
              />
              <label htmlFor="terms-checkbox" className="text-xs text-muted-foreground cursor-pointer">
                I agree to the{' '}
                <Link to="/terms" className="text-primary hover:underline" target="_blank">Terms & Conditions</Link>{' '}and{' '}
                <Link to="/privacy" className="text-primary hover:underline" target="_blank">Privacy Policy</Link>
              </label>
            </div>

            <Button
              type="submit" disabled={loading || !agreedTerms}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded h-12 font-bold uppercase tracking-wide glow-primary disabled:opacity-50"
              data-testid="register-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Deploy Agent<ArrowRight className="ml-2 w-4 h-4" /></>}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
